import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

class PDFExportService {
  constructor() {
    this.defaultOptions = {
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      putOnlyUsedFonts: true,
      floatPrecision: 16,
    };
  }

  // Create company header for all reports
  addCompanyHeader(doc, title, subtitle = "") {
    const pageWidth = doc.internal.pageSize.width;

    // Company logo placeholder (you can add actual logo later)
    doc.setFillColor(59, 130, 246); // Blue color
    doc.rect(20, 15, 15, 15, "F");

    // Company info
    doc.setFontSize(20);
    doc.setTextColor(31, 41, 55); // Gray-800
    doc.text("HR Management System", 40, 22);

    doc.setFontSize(12);
    doc.setTextColor(107, 114, 128); // Gray-500
    doc.text("Système de Gestion des Ressources Humaines", 40, 28);

    // Title
    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    doc.text(title, 20, 45);

    if (subtitle) {
      doc.setFontSize(12);
      doc.setTextColor(107, 114, 128);
      doc.text(subtitle, 20, 52);
    }

    // Date and time
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    const now = new Date();
    const dateStr = now.toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    doc.text(`Généré le: ${dateStr}`, pageWidth - 60, 22);

    // Separator line
    doc.setDrawColor(229, 231, 235); // Gray-200
    doc.line(20, 35, pageWidth - 20, 35);

    return 60; // Return Y position after header
  }

  // Add footer with page numbers
  addFooter(doc, pageNumber, totalPages) {
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;

    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);

    // Footer line
    doc.setDrawColor(229, 231, 235);
    doc.line(20, pageHeight - 20, pageWidth - 20, pageHeight - 20);

    // Page number
    doc.text(
      `Page ${pageNumber} sur ${totalPages}`,
      pageWidth - 40,
      pageHeight - 10
    );

    // Company name
    doc.text("HR Management System - Confidentiel", 20, pageHeight - 10);
  }

  // Export daily attendance report
  async exportDailyReport(attendanceData, stats, date = new Date()) {
    const doc = new jsPDF(this.defaultOptions);

    const title = "Rapport de Présence Quotidien";
    const subtitle = `Date: ${date.toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}`;

    let yPosition = this.addCompanyHeader(doc, title, subtitle);
    yPosition += 10;

    // Summary statistics
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Résumé de la journée", 20, yPosition);
    yPosition += 10;

    const summaryData = [
      ["Total employés", stats.totalEmployees.toString()],
      [
        "Présents",
        `${stats.presentToday} (${(
          (stats.presentToday / stats.totalEmployees) *
          100
        ).toFixed(1)}%)`,
      ],
      [
        "Absents",
        `${stats.absentToday} (${(
          (stats.absentToday / stats.totalEmployees) *
          100
        ).toFixed(1)}%)`,
      ],
      [
        "En retard",
        `${stats.lateToday} (${(
          (stats.lateToday / stats.totalEmployees) *
          100
        ).toFixed(1)}%)`,
      ],
      ["Heures moyennes", `${stats.avgWorkingHours}h`],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Métrique", "Valeur"]],
      body: summaryData,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      margin: { left: 20, right: 20 },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // Employee attendance table
    doc.setFontSize(14);
    doc.text("Détail des présences", 20, yPosition);
    yPosition += 10;

    const employeeTableData = attendanceData.map((emp) => [
      emp.name || "N/A",
      emp.department || "N/A",
      emp.clockIn || "Non pointé",
      emp.clockOut || "En cours",
      emp.status === "present"
        ? "Présent"
        : emp.status === "late"
        ? "En retard"
        : "Absent",
      emp.workingHours ? `${emp.workingHours}h` : "N/A",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [
        ["Employé", "Département", "Arrivée", "Départ", "Statut", "Heures"],
      ],
      body: employeeTableData,
      theme: "striped",
      styles: {
        fontSize: 9,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        4: {
          // Status column
          cellWidth: 20,
          halign: "center",
        },
      },
      margin: { left: 20, right: 20 },
    });

    // Add page numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.addFooter(doc, i, totalPages);
    }

    // Save the PDF
    const fileName = `rapport_presence_${date.toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);

    return fileName;
  }

  // Export monthly summary report
  async exportMonthlySummary(monthlyData, stats, month, year) {
    const doc = new jsPDF(this.defaultOptions);

    const title = "Rapport Mensuel de Présence";
    const subtitle = `${new Date(year, month - 1).toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    })}`;

    let yPosition = this.addCompanyHeader(doc, title, subtitle);
    yPosition += 10;

    // Monthly overview
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Aperçu mensuel", 20, yPosition);
    yPosition += 10;

    const monthlyOverview = [
      ["Jours ouvrables", "22"],
      [
        "Taux de présence moyen",
        `${((stats.presentToday / stats.totalEmployees) * 100).toFixed(1)}%`,
      ],
      [
        "Total heures travaillées",
        `${(stats.avgWorkingHours * 22 * stats.totalEmployees).toFixed(0)}h`,
      ],
      ["Heures supplémentaires", `${stats.overtimeThisMonth}h`],
      ["Retards moyens par jour", `${(stats.lateToday / 22).toFixed(1)}`],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Indicateur", "Valeur"]],
      body: monthlyOverview,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: 255,
        fontStyle: "bold",
      },
      margin: { left: 20, right: 20 },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // Department analysis
    doc.setFontSize(14);
    doc.text("Analyse par département", 20, yPosition);
    yPosition += 10;

    const departmentData = [
      ["IT", "95.2%", "8.2h", "2.5h"],
      ["RH", "92.8%", "7.8h", "1.2h"],
      ["Finance", "97.1%", "8.0h", "3.1h"],
      ["Marketing", "88.5%", "7.5h", "0.8h"],
      ["Ventes", "90.3%", "8.1h", "4.2h"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Département", "Taux présence", "Heures moy.", "Heures sup."]],
      body: departmentData,
      theme: "striped",
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: 255,
        fontStyle: "bold",
      },
      margin: { left: 20, right: 20 },
    });

    // Add charts placeholder
    yPosition = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.text("Graphiques et tendances disponibles en ligne", 20, yPosition);

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.addFooter(doc, i, totalPages);
    }

    const fileName = `rapport_mensuel_${year}_${month
      .toString()
      .padStart(2, "0")}.pdf`;
    doc.save(fileName);

    return fileName;
  }

  // Export employee individual report
  async exportEmployeeReport(employeeData, attendanceHistory) {
    console.log(employeeData);

    const doc = new jsPDF(this.defaultOptions);

    const title = "Rapport Individuel de Présence";
    const subtitle = `Employé: ${employeeData.name} - ${employeeData.department}`;

    let yPosition = this.addCompanyHeader(doc, title, subtitle);
    yPosition += 10;

    // Employee info
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Informations employé", 20, yPosition);
    yPosition += 10;

    const employeeInfo = [
      ["Nom complet", employeeData.name || "N/A"],
      ["Département", employeeData.department || "N/A"],
      ["Poste", employeeData.position || "N/A"],
      ["Date d'embauche", employeeData.hireDate || "N/A"],
      ["Statut", employeeData.status || "Actif"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Information", "Valeur"]],
      body: employeeInfo,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [139, 69, 19],
        textColor: 255,
        fontStyle: "bold",
      },
      margin: { left: 20, right: 20 },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // Performance metrics
    doc.setFontSize(14);
    doc.text("Métriques de performance", 20, yPosition);
    yPosition += 10;

    const performanceData = [
      ["Taux de présence", "94.5%"],
      ["Retards ce mois", "3"],
      ["Heures moyennes/jour", "8.2h"],
      ["Heures supplémentaires", "12.5h"],
      ["Congés pris", "5 jours"],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Métrique", "Valeur"]],
      body: performanceData,
      theme: "striped",
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [139, 69, 19],
        textColor: 255,
        fontStyle: "bold",
      },
      margin: { left: 20, right: 20 },
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.addFooter(doc, i, totalPages);
    }

    const fileName = `rapport_employe_${employeeData.name?.replace(
      /\s+/g,
      "_"
    )}_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);

    return fileName;
  }

  // Capture HTML element as PDF (for dashboard exports)
  async exportDashboardSnapshot(
    elementId,
    filename = "dashboard_snapshot.pdf"
  ) {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error("Element not found");
    }

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 297; // A4 landscape width
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      this.addCompanyHeader(doc, "Capture du Tableau de Bord", "");

      doc.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        10,
        60,
        imgWidth - 20,
        imgHeight
      );

      this.addFooter(doc, 1, 1);

      doc.save(filename);
      return filename;
    } catch (error) {
      console.error("Error capturing dashboard:", error);
      throw error;
    }
  }

  // Export custom report with user-defined parameters
  async exportCustomReport(data, config) {
    const doc = new jsPDF(this.defaultOptions);

    const title = config.title || "Rapport Personnalisé";
    const subtitle = config.subtitle || "";

    let yPosition = this.addCompanyHeader(doc, title, subtitle);
    yPosition += 10;

    // Add custom sections based on config
    if (config.sections) {
      for (const section of config.sections) {
        doc.setFontSize(14);
        doc.setTextColor(31, 41, 55);
        doc.text(section.title, 20, yPosition);
        yPosition += 10;

        if (section.type === "table" && section.data) {
          autoTable(doc, {
            startY: yPosition,
            head: [section.headers],
            body: section.data,
            theme: "striped",
            styles: {
              fontSize: 9,
              cellPadding: 2,
            },
            headStyles: {
              fillColor: [99, 102, 241],
              textColor: 255,
              fontStyle: "bold",
            },
            margin: { left: 20, right: 20 },
          });
          yPosition = doc.lastAutoTable.finalY + 15;
        }
      }
    }

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.addFooter(doc, i, totalPages);
    }

    const fileName =
      config.filename || `rapport_personnalise_${Date.now()}.pdf`;
    doc.save(fileName);

    return fileName;
  }

  // Export leave management report
  async exportLeaveReport(data) {
    const {
      leaveRequests = [],
      leaveTypes = [],
      stats = {},
      user = {},
      dateRange = {},
    } = data;

    const doc = new jsPDF(this.defaultOptions);
    const pageWidth = doc.internal.pageSize.width;

    // Header
    const title = "Rapport de Gestion des Congés";
    const subtitle = `Période du ${
      dateRange.from
        ? new Date(dateRange.from).toLocaleDateString("fr-FR")
        : "Début"
    } au ${
      dateRange.to ? new Date(dateRange.to).toLocaleDateString("fr-FR") : "Fin"
    }`;
    this.addCompanyHeader(doc, title, subtitle);

    let yPosition = 65;

    // Statistics section
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Statistiques générales", 20, yPosition);
    yPosition += 10;

    const statsData = [
      ["Demandes en attente", stats.pendingRequests || 0],
      ["Demandes approuvées", stats.approvedRequests || 0],
      ["Jours utilisés au total", stats.totalDaysUsed || 0],
      ["Jours restants", stats.remainingDays || 0],
      ["Congés à venir", stats.upcomingLeaves || 0],
      ["Équipe actuellement absente", stats.teamOnLeave || 0],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["Métrique", "Valeur"]],
      body: statsData,
      theme: "grid",
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: "bold",
      },
      margin: { left: 20, right: 20 },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 60, halign: "center" },
      },
    });

    yPosition = doc.lastAutoTable.finalY + 20;

    // Leave requests section
    if (leaveRequests && leaveRequests.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text("Demandes de congés", 20, yPosition);
      yPosition += 10;

      const requestsData = leaveRequests.map((request) => [
        `${request.employe?.prenom || ""} ${request.employe?.nom || ""}`,
        request.type_conge?.nom || "",
        new Date(request.date_debut).toLocaleDateString("fr-FR"),
        new Date(request.date_fin).toLocaleDateString("fr-FR"),
        request.nombre_jours || 0,
        this.getStatusLabel(request.statut),
        request.urgence ? "Oui" : "Non",
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [
          ["Employé", "Type", "Début", "Fin", "Jours", "Statut", "Urgent"],
        ],
        body: requestsData,
        theme: "striped",
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: "bold",
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 30 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 15, halign: "center" },
          5: { cellWidth: 25 },
          6: { cellWidth: 15, halign: "center" },
        },
        margin: { left: 20, right: 20 },
      });

      yPosition = doc.lastAutoTable.finalY + 20;
    }

    // Leave types summary
    if (leaveTypes && leaveTypes.length > 0) {
      // Add new page if needed
      if (yPosition > 230) {
        doc.addPage();
        yPosition = 30;
      }

      doc.setFontSize(14);
      doc.setTextColor(31, 41, 55);
      doc.text("Types de congés disponibles", 20, yPosition);
      yPosition += 10;

      const typesData = leaveTypes.map((type) => [
        type.nom || "",
        type.jours_autorises_par_an
          ? `${type.jours_autorises_par_an} jours`
          : "Illimité",
        type.actif ? "Actif" : "Inactif",
      ]);

      autoTable(doc, {
        startY: yPosition,
        head: [["Type de congé", "Jours autorisés/an", "Statut"]],
        body: typesData,
        theme: "grid",
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 50, halign: "center" },
          2: { cellWidth: 40, halign: "center" },
        },
        margin: { left: 20, right: 20 },
      });
    }

    // Footer with user info
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      this.addFooter(doc, i, totalPages);

      // Add user info on first page
      if (i === 1 && user.nom) {
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text(
          `Généré par: ${user.prenom} ${user.nom} (${user.role})`,
          20,
          285
        );
      }
    }

    const fileName = `rapport_conges_${new Date().getTime()}.pdf`;
    doc.save(fileName);

    return fileName;
  }

  // Helper method to get status label in French
  getStatusLabel(status) {
    const statusMap = {
      en_attente: "En attente",
      approuve: "Approuvé",
      refuse: "Refusé",
      annule: "Annulé",
    };
    return statusMap[status] || status;
  }

  // Generate payslip PDF
  async exportPayslip(payslip) {
    console.log(payslip);

    const doc = new jsPDF(this.defaultOptions);

    // Add company header
    this.addCompanyHeader(
      doc,
      "Bulletin de Paie",
      `Période: ${new Date(payslip.periode_debut).toLocaleDateString(
        "fr-FR"
      )} - ${new Date(payslip.periode_fin).toLocaleDateString("fr-FR")}`
    );

    let yPosition = 70;

    // Employee Information Section
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Informations Employé", 20, yPosition);

    yPosition += 10;
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);

    const employeeInfo = [
      [
        `Nom:`,
        `${payslip.employe?.prenom || ""} ${payslip.employe?.nom || ""}`,
      ],
      [`Numéro Employé:`, payslip.employe?.numero_employe || ""],
      [`Email:`, payslip.employe?.email || ""],
      [`Département:`, payslip.employe?.departement?.nom || ""],
      [
        `Date d'embauche:`,
        payslip.employe?.date_embauche
          ? new Date(payslip.employe.date_embauche).toLocaleDateString("fr-FR")
          : "",
      ],
    ];

    employeeInfo.forEach(([label, value]) => {
      doc.text(label, 20, yPosition);
      doc.text(value, 60, yPosition);
      yPosition += 6;
    });

    yPosition += 10;

    // Payslip Details Section
    doc.setFontSize(14);
    doc.setTextColor(31, 41, 55);
    doc.text("Détails de la Paie", 20, yPosition);

    yPosition += 15;

    // Create earnings and deductions table
    const tableData = [];

    // Add salary components
    if (payslip.salaire_base) {
      tableData.push([
        "Salaire de Base",
        "",
        `${parseFloat(payslip.salaire_base).toFixed(2)} MAD`,
      ]);
    }
    if (payslip.heures_supplementaires && payslip.heures_supplementaires > 0) {
      tableData.push([
        "Heures Supplémentaires",
        "",
        `${parseFloat(payslip.heures_supplementaires).toFixed(2)} MAD`,
      ]);
    }
    if (payslip.primes && payslip.primes > 0) {
      tableData.push([
        "Primes",
        "",
        `${parseFloat(payslip.primes).toFixed(2)} MAD`,
      ]);
    }

    // Add total gross
    if (payslip.salaire_brut) {
      tableData.push(["", "", ""]);
      tableData.push([
        "TOTAL BRUT",
        "",
        `${parseFloat(payslip.salaire_brut).toFixed(2)} MAD`,
      ]);
      tableData.push(["", "", ""]);
    }

    // Add detailed deductions (Moroccan payroll specific)
    if (payslip.frais_professionnels && payslip.frais_professionnels > 0) {
      tableData.push([
        "Frais Professionnels (20%)",
        `${parseFloat(payslip.frais_professionnels).toFixed(2)} MAD`,
        "",
      ]);
    }

    if (payslip.cotisations_cnss && payslip.cotisations_cnss > 0) {
      tableData.push([
        "Cotisations CNSS (4.29%)",
        `${parseFloat(payslip.cotisations_cnss).toFixed(2)} MAD`,
        "",
      ]);
    }

    if (payslip.cotisations_amo && payslip.cotisations_amo > 0) {
      tableData.push([
        "Cotisations AMO (2.26%)",
        `${parseFloat(payslip.cotisations_amo).toFixed(2)} MAD`,
        "",
      ]);
    }

    if (payslip.cotisations_retraite && payslip.cotisations_retraite > 0) {
      tableData.push([
        "Cotisations Retraite",
        `${parseFloat(payslip.cotisations_retraite).toFixed(2)} MAD`,
        "",
      ]);
    }

    // Add total social contributions
    if (payslip.cotisations_sociales && payslip.cotisations_sociales > 0) {
      tableData.push([
        "Total Cotisations Sociales",
        `${parseFloat(payslip.cotisations_sociales).toFixed(2)} MAD`,
        "",
      ]);
      tableData.push(["", "", ""]);
    }

    // Add income tax details
    if (payslip.ir_brut && payslip.ir_brut > 0) {
      tableData.push([
        "Impôt sur le Revenu (IR)",
        `${parseFloat(payslip.ir_brut).toFixed(2)} MAD`,
        "",
      ]);
    }

    if (payslip.deduction_personnes && payslip.deduction_personnes > 0) {
      tableData.push([
        "Déduction Personnes à Charge",
        `-${parseFloat(payslip.deduction_personnes).toFixed(2)} MAD`,
        "",
      ]);
    }

    if (payslip.ir_net && payslip.ir_net > 0) {
      tableData.push([
        "IR Net",
        `${parseFloat(payslip.ir_net).toFixed(2)} MAD`,
        "",
      ]);
    }

    if (payslip.autres_deductions && payslip.autres_deductions > 0) {
      tableData.push([
        "Autres Déductions",
        `${parseFloat(payslip.autres_deductions).toFixed(2)} MAD`,
        "",
      ]);
    }

    // Calculate and add total deductions
    const totalDeductions =
      (parseFloat(payslip.cotisations_sociales) || 0) +
      (parseFloat(payslip.ir_net) || 0) +
      (parseFloat(payslip.autres_deductions) || 0);

    if (totalDeductions > 0) {
      tableData.push(["", "", ""]);
      tableData.push([
        "TOTAL DÉDUCTIONS",
        `${totalDeductions.toFixed(2)} MAD`,
        "",
      ]);
      tableData.push(["", "", ""]);
    }

    // Add net salary
    if (payslip.salaire_net) {
      tableData.push([
        "SALAIRE NET",
        "",
        `${parseFloat(payslip.salaire_net).toFixed(2)} MAD`,
      ]);
    }

    autoTable(doc, {
      startY: yPosition,
      head: [["Libellé", "Déductions", "Gains"]],
      body: tableData,
      theme: "grid",
      styles: {
        fontSize: 10,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 50, halign: "right" },
        2: { cellWidth: 50, halign: "right" },
      },
      didParseCell: function (data) {
        if (
          data.cell.text[0] &&
          (data.cell.text[0].includes("TOTAL") ||
            data.cell.text[0].includes("SALAIRE NET"))
        ) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [243, 244, 246];
        }
      },
    });

    // Add footer with status and dates
    const finalY = doc.lastAutoTable.finalY + 20;

    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);

    // Status
    const statusLabels = {
      brouillon: "Brouillon",
      valide: "Validé",
      paye: "Payé",
    };
    doc.text(
      `Statut: ${statusLabels[payslip.statut] || payslip.statut}`,
      20,
      finalY
    );

    // Generation date
    if (payslip.date_generation) {
      doc.text(
        `Date de génération: ${new Date(
          payslip.date_generation
        ).toLocaleDateString("fr-FR")}`,
        20,
        finalY + 6
      );
    }

    // Add page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text(
        `Page ${i} sur ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    // Save the PDF
    const fileName = `bulletin_paie_${
      payslip.employe?.nom || "employee"
    }_${new Date(payslip.periode_debut)
      .toLocaleDateString("fr-FR")
      .replace(/\//g, "-")}.pdf`;
    doc.save(fileName);
  }

  // Generate wire transfer order (Ordre de virement)
  async exportWireTransferOrder(employees) {
    const doc = new jsPDF(this.defaultOptions);

    // Filter only active employees
    const activeEmployees = employees.filter(
      (emp) => emp.statut_employe === "actif"
    );

    if (activeEmployees.length === 0) {
      throw new Error("Aucun employé actif trouvé");
    }

    // Title and date
    let yPosition = 20;
    
    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    doc.text("Ordre de Virement", 20, yPosition);
    
    yPosition += 8;
    doc.setFontSize(11);
    doc.setTextColor(107, 114, 128);
    doc.text(
      `Date: ${new Date().toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })}`,
      20,
      yPosition
    );
    
    yPosition += 6;
    doc.text("Société : Omnidoc Assist", 20, yPosition);
    
    yPosition += 12;

    // Calculate total
    const totalSalaries = activeEmployees.reduce(
      (sum, emp) => sum + (emp.salaire ? parseFloat(emp.salaire) : 0),
      0
    );

    // Employee list table
    const employeeTableData = activeEmployees.map((emp) => [
      `${emp.prenom || ""} ${emp.nom || ""}`.trim(),
      emp.iban || "MANQUANT",
      emp.salaire && parseFloat(emp.salaire) > 0 
        ? `${parseFloat(emp.salaire).toFixed(2)} MAD` 
        : "MANQUANT",
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Nom", "RIB", "Salaire"]],
      body: employeeTableData,
      theme: "striped",
      styles: {
        fontSize: 10,
        cellPadding: 4,
      },
      headStyles: {
        fillColor: [16, 185, 129],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 70 },
        2: { cellWidth: 50, halign: "right" },
      },
      margin: { left: 20, right: 20 },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      didParseCell: function (data) {
        // Highlight "MANQUANT" in red
        if (data.cell.text[0] === "MANQUANT") {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [254, 226, 226];
        }
      },
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // Total section
    doc.setFillColor(16, 185, 129);
    doc.rect(20, yPosition, doc.internal.pageSize.width - 40, 15, "F");

    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text("TOTAL", 25, yPosition + 10);
    doc.text(
      `${totalSalaries.toFixed(2)} MAD`,
      doc.internal.pageSize.width - 60,
      yPosition + 10
    );

    // Save the PDF
    const fileName = `ordre_virement_${new Date()
      .toISOString()
      .split("T")[0]}.pdf`;
    doc.save(fileName);

    return fileName;
  }
}

export const pdfExportService = new PDFExportService();

// Export individual methods with proper binding to maintain 'this' context
export const exportDailyReport =
  pdfExportService.exportDailyReport.bind(pdfExportService);
export const exportMonthlySummary =
  pdfExportService.exportMonthlySummary.bind(pdfExportService);
export const exportCustomReport =
  pdfExportService.exportCustomReport.bind(pdfExportService);
export const exportDashboardSnapshot =
  pdfExportService.exportDashboardSnapshot.bind(pdfExportService);
export const exportLeaveReport =
  pdfExportService.exportLeaveReport.bind(pdfExportService);
export const exportPayslip =
  pdfExportService.exportPayslip.bind(pdfExportService);
export const exportWireTransferOrder =
  pdfExportService.exportWireTransferOrder.bind(pdfExportService);

export default pdfExportService;
